import { computed, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import {
  addEntity,
  entityConfig,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  withCalls,
  withEntitiesCalls,
  withLogger,
  withEntitiesLocalSort,
  callConfig,
  entityCallConfig,
} from '@ngrx-traits/signals';
import { map } from 'rxjs';
import { ViewConfiguratorService } from '../data/view-configurator.service';
import { UiLayoutViewDto } from '../models/view-configurator-dto.model';
import { DataTypeStore } from '@app/features/options/data-type';
import {
  caseFieldRegistry,
  CaseSectionId,
} from '@app/features/case-data/case-field-registry';
import { FormConfiguratorDomain } from './unified-field-grid-config.store';

export interface UiLayoutViewWithSection extends UiLayoutViewDto {
  sectionLabel: string;
  domain: FormConfiguratorDomain | null;
}

const viewsEntityConfig = entityConfig({
  entity: type<UiLayoutViewDto>(),
  collection: 'views',
  selectId: (view: UiLayoutViewDto) => String(view.id),
});

export const ViewConfigStore = signalStore(
  withEntities(viewsEntityConfig),

  withEntitiesLocalSort({
    entity: type<UiLayoutViewDto>(),
    collection: 'views',
    defaultSort: { field: 'order', direction: 'asc' },
  }),

  withComputed(({ viewsEntities }) => {
    const dataTypeStore = inject(DataTypeStore);

    const getDataTypeId = (sectionId: CaseSectionId): number | null =>
      dataTypeStore.getDataTypeIdByName(
        caseFieldRegistry.getSection(sectionId).dtoName
      );

    const getDomainForId = (dataTypeId: number): CaseSectionId | null => {
      const name = dataTypeStore.getDataTypeNameById(dataTypeId);
      if (!name) return null;
      const section = caseFieldRegistry.getSectionByDtoName(name);
      return section?.id ?? null;
    };

    return {
      caseDataTypeId: computed(() => getDataTypeId('case')),
      vehicleDataTypeId: computed(() => getDataTypeId('vehicle')),
      valuationDataTypeId: computed(() => getDataTypeId('valuation')),
      calculationDataTypeId: computed(() => getDataTypeId('calculation')),

      caseViews: computed(() => {
        const caseId = getDataTypeId('case');
        if (!caseId) return [];
        return viewsEntities().filter((view) => view.dataTypeId === caseId);
      }),

      tenantCaseViews: computed(() => {
        const caseId = getDataTypeId('case');
        if (!caseId) return [];
        return viewsEntities().filter((view) => view.dataTypeId === caseId);
      }),

      vehicleViews: computed(() => {
        const vehicleId = getDataTypeId('vehicle');
        if (!vehicleId) return [];
        return viewsEntities().filter((view) => view.dataTypeId === vehicleId);
      }),

      tenantVehicleViews: computed(() => {
        const vehicleId = getDataTypeId('vehicle');
        if (!vehicleId) return [];
        return viewsEntities().filter((view) => view.dataTypeId === vehicleId);
      }),

      valuationViews: computed(() => {
        const valuationId = getDataTypeId('valuation');
        if (!valuationId) return [];
        return viewsEntities().filter(
          (view) => view.dataTypeId === valuationId
        );
      }),

      tenantValuationViews: computed(() => {
        const valuationId = getDataTypeId('valuation');
        if (!valuationId) return [];
        return viewsEntities().filter(
          (view) => view.dataTypeId === valuationId
        );
      }),

      calculationViews: computed(() => {
        const calculationId = getDataTypeId('calculation');
        if (!calculationId) return [];
        return viewsEntities().filter(
          (view) => view.dataTypeId === calculationId
        );
      }),

      tenantCalculationViews: computed(() => {
        const calculationId = getDataTypeId('calculation');
        if (!calculationId) return [];
        return viewsEntities().filter(
          (view) => view.dataTypeId === calculationId
        );
      }),

      caseViewNames: computed(() => {
        const caseId = getDataTypeId('case');
        if (!caseId) return undefined;
        return viewsEntities()
          .filter(
            (v) => v.dataTypeId === caseId && v.isActive && v.dataViewName
          )
          .map((v) => v.dataViewName!);
      }),

      vehicleViewNames: computed(() => {
        const vehicleId = getDataTypeId('vehicle');
        if (!vehicleId) return undefined;
        return viewsEntities()
          .filter(
            (v) => v.dataTypeId === vehicleId && v.isActive && v.dataViewName
          )
          .map((v) => v.dataViewName!);
      }),

      viewsCount: computed(() => viewsEntities().length),
      hasViews: computed(() => viewsEntities().length > 0),
      caseViewsCount: computed(() => {
        const caseId = getDataTypeId('case');
        if (!caseId) return 0;
        return viewsEntities().filter((view) => view.dataTypeId === caseId)
          .length;
      }),
      vehicleViewsCount: computed(() => {
        const vehicleId = getDataTypeId('vehicle');
        if (!vehicleId) return 0;
        return viewsEntities().filter((view) => view.dataTypeId === vehicleId)
          .length;
      }),
      valuationViewsCount: computed(() => {
        const valuationId = getDataTypeId('valuation');
        if (!valuationId) return 0;
        return viewsEntities().filter((view) => view.dataTypeId === valuationId)
          .length;
      }),
      calculationViewsCount: computed(() => {
        const calculationId = getDataTypeId('calculation');
        if (!calculationId) return 0;
        return viewsEntities().filter(
          (view) => view.dataTypeId === calculationId
        ).length;
      }),

      allViewsWithSection: computed((): UiLayoutViewWithSection[] => {
        return viewsEntities().map((view) => {
          const domain = getDomainForId(view.dataTypeId);
          const sectionLabel = domain
            ? caseFieldRegistry.getSection(domain).label
            : 'Unbekannt';

          return {
            ...view,
            domain,
            sectionLabel,
          };
        });
      }),

      sectionOptions: computed(() => {
        const options: { value: CaseSectionId; label: string }[] = [];
        // Exclude detailedCalculation from section options as it's a nested section
        const sections: CaseSectionId[] = [
          'case',
          'vehicle',
          'valuation',
          'calculation',
          'infoPage',
        ];
        for (const sectionId of sections) {
          if (getDataTypeId(sectionId)) {
            options.push({
              value: sectionId,
              label: caseFieldRegistry.getSection(sectionId).label,
            });
          }
        }
        return options;
      }),
    };
  }),

  withMethods((store) => {
    const dataTypeStore = inject(DataTypeStore);
    return {
      getDataTypeId(type: FormConfiguratorDomain): number | null {
        return dataTypeStore.getDataTypeIdByName(
          caseFieldRegistry.getSection(type).dtoName
        );
      },

      getViewsByType(type: FormConfiguratorDomain): UiLayoutViewDto[] {
        const dataTypeId = dataTypeStore.getDataTypeIdByName(
          caseFieldRegistry.getSection(type).dtoName
        );
        if (!dataTypeId) return [];
        return store
          .viewsEntities()
          .filter((view) => view.dataTypeId === dataTypeId);
      },

      getDomainForDataTypeId(
        dataTypeId: number
      ): FormConfiguratorDomain | null {
        const name = dataTypeStore.getDataTypeNameById(dataTypeId);
        if (!name) return null;

        const section = caseFieldRegistry.getSectionByDtoName(name);
        return section?.id ?? null;
      },

      getPathPrefixForDomain(domain: FormConfiguratorDomain): string {
        return caseFieldRegistry.getSection(domain).pathPrefix;
      },

      getLabelForDomain(domain: FormConfiguratorDomain): string {
        return caseFieldRegistry.getSection(domain).label;
      },
    };
  }),

  withCalls((store) => {
    const viewConfiguratorService = inject(ViewConfiguratorService);
    const dataTypeStore = inject(DataTypeStore);
    const snackBar = inject(MatSnackBar);
    return {
      loadViews: callConfig({
        call: () => viewConfiguratorService.loadViews(),
        callWith: () => dataTypeStore.isDataTypeLoaded(),
        onSuccess: (views) => {
          patchState(store, {
            viewsEntityMap: views.reduce(
              (acc, view) => {
                acc[String(view.id)] = view;
                return acc;
              },
              {} as Record<string, UiLayoutViewDto>
            ),
            viewsIds: views.map((view) => String(view.id)),
          });
        },
      }),

      loadView: callConfig({
        call: ({ viewId }: { viewId: number }) =>
          viewConfiguratorService.getView(viewId),
        onSuccess: (view) => {
          console.log('success', view);
          patchState(store, addEntity(view, viewsEntityConfig));
        },
      }),

      createView: callConfig({
        call: ({
          title,
          isActive,
          dataTypeId,
        }: {
          title: string;
          isActive?: boolean;
          dataTypeId: number;
        }) => {
          const existingViews = store
            .viewsEntities()
            .filter((v) => v.dataTypeId === dataTypeId);
          const maxOrder =
            existingViews.length > 0
              ? Math.max(...existingViews.map((v) => v.order ?? 0))
              : -1;

          return viewConfiguratorService.createView({
            title,
            isActive: isActive ?? false,
            dataTypeId,
            order: maxOrder + 1,
          });
        },
        onSuccess: (newView) => {
          patchState(store, setEntity(newView, viewsEntityConfig));

          snackBar.open('Neue Ansicht erstellt', 'OK', {
            duration: 2000,
          });

          return newView;
        },
      }),
    };
  }),

  withCalls((store) => {
    const viewConfiguratorService = inject(ViewConfiguratorService);
    const snackBar = inject(MatSnackBar);
    return {
      restoreDefaultUiLayout: callConfig({
        call: () => viewConfiguratorService.restoreDefaultUiLayout(),
        onSuccess: () => {
          snackBar.open('Standardeinstellungen wiederhergestellt.', 'OK', {
            duration: 3000,
          });
          store.loadViews();
        },
        onError: () => {
          snackBar.open(
            'Standardeinstellungen wiederherstellen fehlgeschlagen',
            'OK',
            { duration: 3000 }
          );
        },
      }),
    };
  }),

  withEntitiesCalls({
    ...viewsEntityConfig,
    calls: () => {
      const viewConfiguratorService = inject(ViewConfiguratorService);
      const snackBar = inject(MatSnackBar);
      return {
        updateView: entityCallConfig({
          paramsSelectId: (params: {
            id: number;
            title?: string;
            isActive?: boolean;
            order?: number;
            dataTypeId?: number;
          }) => String(params.id),
          call: (params: {
            id: number;
            title?: string;
            isActive?: boolean;
            order?: number;
            dataTypeId?: number;
          }) =>
            viewConfiguratorService
              .updateView(params.id, {
                title: params.title,
                isActive: params.isActive,
                order: params.order,
                dataTypeId: params.dataTypeId,
              })
              .pipe(
                map(() => ({
                  ...(params.title !== undefined && { title: params.title }),
                  ...(params.isActive !== undefined && {
                    isActive: params.isActive,
                  }),
                  ...(params.order !== undefined && { order: params.order }),
                  ...(params.dataTypeId !== undefined && {
                    dataTypeId: params.dataTypeId,
                  }),
                }))
              ),
          onSuccess: () => {
            snackBar.open('Ansicht aktualisiert', 'OK', {
              duration: 2000,
            });
          },
        }),

        deleteView: entityCallConfig({
          paramsSelectId: (params: { id: number }) => String(params.id),
          call: (params: { id: number }) =>
            viewConfiguratorService
              .deleteView(params.id)
              .pipe(map(() => undefined)),
          onSuccess: () => {
            snackBar.open('Ansicht gelöscht', 'OK', { duration: 2000 });
          },
        }),
      };
    },
  }),
  withLogger({
    name: 'ViewConfigStore',
    showDiff: true,
  })
);
